import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { apiClient } from '@/lib/api-client';
import type {
  ClaimTransaction,
  ClaimFinancialSummary,
  CreateTransactionRequest,
  UpdateTransactionRequest,
  UpsertFinancialSummaryRequest,
} from '@/api/generated/client/models';

// ── Query keys ────────────────────────────────────────

const financialKeys = {
  all: (claimId: number) => ['claim-financials', claimId] as const,
  summary: (claimId: number) => [...financialKeys.all(claimId), 'summary'] as const,
  transactions: (claimId: number) => [...financialKeys.all(claimId), 'transactions'] as const,
};

// ── Summary hooks ─────────────────────────────────────

export function useFinancialSummary(claimId: number, options?: { enabled?: boolean }) {
  const { firebaseUser } = useAuth();

  return useQuery<ClaimFinancialSummary | null>({
    queryKey: financialKeys.summary(claimId),
    queryFn: async () => {
      // Backend returns 200 with null body when no summary exists
      // A 404 means claim doesn't exist or unauthorized - let it throw
      return await apiClient.financials.getClaimFinancialSummary({ claimId });
    },
    enabled: !!claimId && !!firebaseUser && (options?.enabled ?? true),
  });
}

export function useUpsertFinancialSummary(claimId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertFinancialSummaryRequest) =>
      apiClient.financials.upsertClaimFinancialSummary({
        claimId,
        upsertFinancialSummaryRequest: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.summary(claimId) });
    },
  });
}

// ── Transactions hooks ────────────────────────────────

export function useTransactions(claimId: number) {
  const { firebaseUser } = useAuth();

  return useQuery<ClaimTransaction[]>({
    queryKey: financialKeys.transactions(claimId),
    queryFn: async () => {
      const response = await apiClient.financials.getClaimTransactions({ claimId });
      return response.items || [];
    },
    enabled: !!claimId && !!firebaseUser,
  });
}

export function useCreateTransaction(claimId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTransactionRequest) =>
      apiClient.financials.createClaimTransaction({
        claimId,
        createTransactionRequest: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.transactions(claimId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.summary(claimId) });
    },
  });
}

export function useUpdateTransaction(claimId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTransactionRequest }) =>
      apiClient.financials.updateClaimTransaction({
        claimId,
        id,
        updateTransactionRequest: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.transactions(claimId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.summary(claimId) });
    },
  });
}

export function useDeleteTransaction(claimId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      apiClient.financials.deleteClaimTransaction({ claimId, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.transactions(claimId) });
      queryClient.invalidateQueries({ queryKey: financialKeys.summary(claimId) });
    },
  });
}

// ── Derived financial computations ────────────────────
// Pure business logic extracted from components so it can be tested and reused.

export interface DerivedFinancials {
  /** Raw or saved values (defaulted to 0 when null) */
  rcvCents: number;
  depreciationCents: number;
  recoverableCents: number;
  nonRecoverableCents: number;
  deductibleCents: number;

  /** Computed → overridable summary values */
  acvCents: number;
  netClaimCents: number;
  netWithRecoverableCents: number;

  /** Transaction aggregates */
  totalPaymentsCents: number;
  totalFeesCollectedCents: number;

  /** Expected fee from fee structure (saved or auto-computed) */
  expectedFeeCents: number;
}

export function deriveFinancials(
  summary: ClaimFinancialSummary | null | undefined,
  transactions: ClaimTransaction[],
): DerivedFinancials {
  const rcvCents = summary?.rcvCents ?? 0;
  const depreciationCents = summary?.depreciationCents ?? 0;
  const recoverableCents = summary?.recoverableDepreciationCents ?? 0;
  const nonRecoverableCents = summary?.nonRecoverableDepreciationCents ?? 0;
  const deductibleCents = summary?.deductibleCents ?? 0;

  // Computed defaults (used when no saved override exists)
  const computedAcvCents = Math.max(0, rcvCents - depreciationCents);
  const computedNetClaimCents = Math.max(0, computedAcvCents - deductibleCents);
  const computedNetWithRecoverableCents = computedNetClaimCents + recoverableCents;

  // Use saved value if present, otherwise fall back to computed
  const acvCents = summary?.acvCents ?? computedAcvCents;
  const netClaimCents = summary?.netClaimCents ?? computedNetClaimCents;
  const netWithRecoverableCents = summary?.netWithRecoverableCents ?? computedNetWithRecoverableCents;

  // Transaction aggregates (excluding voided)
  const active = transactions.filter((t) => t.status !== 'voided');
  const totalPaymentsCents = active.reduce((sum, t) => sum + (t.amountCents ?? 0), 0);
  const totalFeesCollectedCents = active.reduce((sum, t) => sum + (t.feeCents ?? 0), 0);

  // Expected fees
  const computedExpectedFeeCents =
    summary?.feeType === 'percentage' && summary?.feePercentage != null
      ? Math.round(netWithRecoverableCents * (summary.feePercentage / 100))
      : 0;
  const expectedFeeCents = summary?.expectedFeeCents ?? computedExpectedFeeCents;

  return {
    rcvCents,
    depreciationCents,
    recoverableCents,
    nonRecoverableCents,
    deductibleCents,
    acvCents,
    netClaimCents,
    netWithRecoverableCents,
    totalPaymentsCents,
    totalFeesCollectedCents,
    expectedFeeCents,
  };
}

// ── Check file hooks ──────────────────────────────────

export function useUploadCheckFile(claimId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transactionId, file }: { transactionId: number; file: File }) => {
      return new Promise<ClaimTransaction>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = async () => {
          try {
            const base64String = (reader.result as string).split(',')[1];
            const result = await apiClient.financials.uploadTransactionCheckFile({
              claimId,
              id: transactionId,
              uploadCheckFileRequest: {
                filename: file.name,
                mimeType: file.type,
                fileContent: base64String,
              },
            });
            resolve(result);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => {
          reject(reader.error || new Error('Failed to read file'));
        };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.transactions(claimId) });
    },
  });
}

export function useDeleteCheckFile(claimId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: number) =>
      apiClient.financials.deleteTransactionCheckFile({ claimId, id: transactionId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: financialKeys.transactions(claimId) });
    },
  });
}

export function useDownloadCheckFile(claimId: number) {
  return useMutation({
    mutationFn: async ({ transactionId, filename }: { transactionId: number; filename?: string }) => {
      const blob = await apiClient.financials.getTransactionCheckFile({ claimId, id: transactionId });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `check-${transactionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ── End ───────────────────────────────────────────────

